import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  adminApi,
  adminAuth,
  type AdminOrder,
  type ListResponse,
} from '../lib/adminApi';

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<string, string> = {
  PENDING_PAYMENT: 'bg-amber-50 text-amber-700 ring-amber-200',
  PAID: 'bg-blue-50 text-blue-700 ring-blue-200',
  SUBMITTING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ACCEPTED: 'bg-green-50 text-green-700 ring-green-200',
  REJECTED: 'bg-red-50 text-red-700 ring-red-200',
};

/**
 * The Filings page is the dedicated orders list – same table + search +
 * filter that used to sit on the Dashboard. Lifted into its own route so
 * the new Dashboard can focus on overview/today's-work.
 */
export function AdminFilings() {
  const navigate = useNavigate();
  const [data, setData] = useState<ListResponse | null>(null);
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [archive, setArchive] = useState<'active' | 'archived' | 'all'>('active');
  // Hide TEST-* orders by default so the daily view is free of local-dev
  // clutter. Admin can flip it off to inspect test data.
  const [hideTest, setHideTest] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return;
    setLoading(true);
    const handle = window.setTimeout(() => {
      adminApi
        .listOrders({ status: status || undefined, q: q || undefined, page, pageSize: PAGE_SIZE, archive, hideTest })
        .then(setData)
        .catch((e) => setError(String(e.message)))
        .finally(() => setLoading(false));
    }, 200);
    return () => window.clearTimeout(handle);
  }, [status, q, page, archive, hideTest]);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Filings</h1>
          <p className="mt-1 text-sm text-slate-500">
            All filings across all customers – newest first.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search by company, email, order ID…"
          className="input max-w-md flex-1"
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="input max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="PENDING_PAYMENT">Pending payment</option>
          <option value="PAID">Paid</option>
          <option value="SUBMITTING">Submitting</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <div className="inline-flex rounded-md bg-slate-100 p-1 text-xs">
          {(['active', 'archived', 'all'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => { setArchive(opt); setPage(1); }}
              className={`rounded px-3 py-1 capitalize ${
                archive === opt
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={hideTest}
            onChange={(e) => { setHideTest(e.target.checked); setPage(1); }}
            className="h-3.5 w-3.5 rounded border-slate-300"
          />
          Hide test orders
        </label>
        {loading && <span className="text-xs text-slate-400">Loading…</span>}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-right">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.items.map((o) => (
              <OrderRow key={o.id} order={o} />
            ))}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                  No filings match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-slate-500">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of{' '}
            {data.total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-ghost"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * PAGE_SIZE >= data.total}
              className="btn-ghost"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function OrderRow({ order }: { order: AdminOrder }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link to={`/admin/orders/${order.id}`} className="font-mono text-xs text-brand-700 hover:underline">
          {order.id.replace(/^ord_/, '').slice(0, 10)}…
        </Link>
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">{order.companyName}</div>
        <div className="text-xs text-slate-500">{order.companyNumber}</div>
      </td>
      <td className="px-4 py-3 text-slate-600">{order.email}</td>
      <td className="px-4 py-3"><StatusPill status={order.status} /></td>
      <td className="px-4 py-3 text-right tabular-nums">£{(order.amountPence / 100).toFixed(2)}</td>
      <td className="px-4 py-3 text-right text-xs text-slate-500">
        {new Date(order.createdAt).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'short', year: 'numeric',
        })}
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}
